import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Loader2, Volume2, VolumeX } from "lucide-react";
import AudioListen from "@/components/AudioListen";
import { MicButton } from "@/components/MicButton";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getOrCreateSession() {
  try {
    const k = "tacc_luma_session";
    let id = sessionStorage.getItem(k);
    if (!id || !/^luma_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id)) {
      id = "luma_" + crypto.randomUUID();
      sessionStorage.setItem(k, id);
    }
    return id;
  } catch {
    return "luma_" + crypto.randomUUID();
  }
}

const GENERIC_OPENER = "Hi. I'm Luma. Take a breath — no rush. What's sitting heaviest right now?";

export default function LumaChat() {
  const { user } = useAuth();
  const firstName = (user?.name || "").split(" ")[0];
  const opener = firstName
    ? `Hi ${firstName}. I'm Luma — glad you're here. Take a breath, no rush. What's sitting heaviest right now?`
    : GENERIC_OPENER;
  const [messages, setMessages] = useState([{ role: "assistant", content: opener }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(getOrCreateSession);
  const [readAloud, setReadAloud] = useState(() => { try { return localStorage.getItem("luma_read_aloud") === "1"; } catch { return false; } });
  const [lastSpokenIdx, setLastSpokenIdx] = useState(-1);
  const scrollRef = useRef(null);

  const toggleReadAloud = () => {
    setReadAloud((v) => { const nv = !v; try { localStorage.setItem("luma_read_aloud", nv ? "1" : "0"); } catch (e) { console.warn("read-aloud preference not persisted (private mode?):", e); } return nv; });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const replyIdx = messages.length + 1;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/luma/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ detail: "Luma is resting." }));
        setMessages((m) => {
          const cp = [...m];
          cp[cp.length - 1] = { role: "assistant", content: err.detail || "Luma paused. Try again in a moment." };
          return cp;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const cp = [...m];
          cp[cp.length - 1] = { role: "assistant", content: acc };
          return cp;
        });
      }
      if (acc.trim()) setLastSpokenIdx(replyIdx);
    } catch {
      setMessages((m) => {
        const cp = [...m];
        cp[cp.length - 1] = { role: "assistant", content: "Luma lost signal for a moment. Try again when you're ready." };
        return cp;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="max-w-2xl mx-auto" data-testid="luma-chat">
      <div className="rounded-2xl border border-[#E7DFD5] bg-white overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-[#E7DFD5] flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F59E0B]/40 to-[#B45309]/20 border border-[#F59E0B]/30 flex items-center justify-center shrink-0">
            <Sparkles size={16} className="text-[#B45309]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C]">Luma</div>
            <div className="text-xs text-[#A8A29E]">Anonymous · Private · Here for you</div>
          </div>
          <button
            type="button"
            onClick={toggleReadAloud}
            aria-pressed={readAloud}
            data-testid="luma-read-aloud"
            title={readAloud ? "Luma reads each reply aloud — tap to turn off" : "Let Luma read her replies aloud"}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border font-mono text-[9px] tracking-[0.2em] uppercase transition-colors ${readAloud ? "border-[#F59E0B] text-[#B45309] bg-[#F59E0B]/10" : "border-[#E7DFD5] text-[#78716C] hover:border-[#F59E0B]/60"}`}
          >
            {readAloud ? <Volume2 size={13} /> : <VolumeX size={13} />} {readAloud ? "Reading aloud" : "Read aloud"}
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="p-5 max-h-[420px] overflow-y-auto space-y-4" data-testid="luma-messages">
          <AnimatePresence initial={false}>
            {messages.map((m, i) => (
              <motion.div
                key={`${m.role}-${i}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.65, 0, 0.35, 1] }}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
                data-testid={`luma-msg-${m.role}`}
              >
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#F59E0B]/15 border border-[#F59E0B]/25 rounded-br-sm text-[#1C1917]"
                    : "bg-black/5 border border-[#E7DFD5] rounded-bl-sm text-[#44403C]"
                }`}>
                  {m.content || (busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin text-[#B45309]" /> : "")}
                  {m.role === "assistant" && m.content && !(busy && i === messages.length - 1) && (
                    <div className="mt-2 pt-2 border-t border-[#E7DFD5]">
                      <AudioListen
                        key={`${i}-${readAloud && i === lastSpokenIdx ? "auto" : "manual"}`}
                        text={m.content}
                        label="Listen"
                        testid={`luma-listen-${i}`}
                        autoPlay={readAloud && i === lastSpokenIdx}
                        speakerLabel="Luma · speaking"
                        speakerAvatar={null}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Input */}
        <div className="p-4 border-t border-[#E7DFD5]">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              placeholder="Whatever's weighing on you. No rush."
              data-testid="luma-input"
              className="flex-1 bg-transparent border border-[#E7DFD5] px-3 py-2.5 rounded-xl text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B]/60 transition-colors resize-none"
            />
            <MicButton
              testId="luma-mic"
              rounded
              size={40}
              context="Someone speaking gently to Luma, a companion, about feeling burnt out."
              onText={(t) => setInput((v) => (v ? `${v} ${t}` : t))}
              disabled={busy}
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              data-testid="luma-send"
              aria-label="Send"
              className="w-10 h-10 rounded-full bg-[#F59E0B] text-black flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
            </button>
          </div>
          <p className="mt-2 font-mono text-[9px] tracking-[0.25em] uppercase text-[#A8A29E] text-center">
            Luma is a companion, not a therapist. In crisis? Call or text 988.
          </p>
        </div>
      </div>
    </div>
  );
}
